'use client'

import { useEffect, useState } from 'react'
import { useAuth } from '@/store/hooks'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
    getPriceTables,
    deletePriceTable,
    PriceTable,
    PRICE_TABLE_TYPE_LABELS,
    PRICE_TABLE_STATUS_LABELS,
    PRICE_TABLE_STATUS_COLORS,
} from '@/lib/api/billing/price-table-api'
import {
    MdTableChart,
    MdSearch,
    MdClose,
    MdAdd,
    MdEdit,
    MdDelete,
    MdContentCopy,
    MdArrowBack,
} from 'react-icons/md'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'

export default function PriceTablesPage() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { canCreate, canUpdate, canDelete } = useResourcePermissions('billing', 'price_tables')
    const [priceTables, setPriceTables] = useState<PriceTable[]>([])
    const [loading, setLoading] = useState(true)
    const [showSearch, setShowSearch] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const [typeFilter, setTypeFilter] = useState<string>('all')
    const [statusFilter, setStatusFilter] = useState<string>('all')
    const [deleteId, setDeleteId] = useState<number | null>(null)

    useEffect(() => {
        if (user?.companyId) {
            loadPriceTables()
        }
    }, [user?.companyId])

    const loadPriceTables = async () => {
        setLoading(true)
        try {
            const response = await getPriceTables()
            if (response.success) {
                setPriceTables(response.result || [])
            } else {
                toast.error(response.error || 'Erro ao carregar tabelas')
            }
        } catch (error) {
            toast.error('Erro ao carregar tabelas')
        } finally {
            setLoading(false)
        }
    }

    const handleDelete = async (id: number) => {
        try {
            const response = await deletePriceTable(id)
            if (response.success) {
                toast.success('Tabela excluída com sucesso')
                loadPriceTables()
            } else {
                toast.error(response.error || 'Erro ao excluir tabela')
            }
        } catch (error) {
            toast.error('Erro ao excluir tabela')
        } finally {
            setDeleteId(null)
        }
    }

    const filteredTables = priceTables.filter(table => {
        if (searchTerm) {
            const search = searchTerm.toLowerCase()
            if (
                !table.name.toLowerCase().includes(search) &&
                !table.healthPlan?.name?.toLowerCase().includes(search)
            ) {
                return false
            }
        }
        if (typeFilter !== 'all' && table.type !== typeFilter) {
            return false
        }
        if (statusFilter !== 'all' && table.status !== statusFilter) {
            return false
        }
        return true
    })

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando tabelas de preços...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6 p-4">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/billing')}
                        className="btn btn-ghost btn-sm"
                    >
                        <MdArrowBack className="text-lg" />
                    </button>
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg">
                        <MdTableChart className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            Tabelas de Preços
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {priceTables.length}{' '}
                            {priceTables.length === 1 ? 'tabela' : 'tabelas'}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {showSearch ? (
                        <div className="flex items-center gap-2">
                            <input
                                type="text"
                                placeholder="Buscar tabelas..."
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                className="input input-bordered input-sm w-64"
                                autoFocus
                            />
                            <button
                                type="button"
                                onClick={() => {
                                    setShowSearch(false)
                                    setSearchTerm('')
                                }}
                                className="btn btn-sm btn-ghost"
                            >
                                <MdClose className="text-lg" />
                            </button>
                        </div>
                    ) : (
                        <>
                            <button
                                onClick={() => setShowSearch(true)}
                                className="btn btn-sm btn-ghost gap-2"
                            >
                                <MdSearch className="text-lg" />
                                <span className="hidden sm:inline">Buscar</span>
                            </button>
                            {canCreate && (
                                <button
                                    onClick={() =>
                                        navigate('/billing/price-tables/new')
                                    }
                                    className="btn btn-secondary gap-2"
                                >
                                    <MdAdd className="text-lg" />
                                    <span className="hidden sm:inline">
                                        Nova Tabela
                                    </span>
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Filters */}
            <div className="flex gap-4 flex-wrap">
                <select
                    value={typeFilter}
                    onChange={e => setTypeFilter(e.target.value)}
                    className="select select-bordered select-sm"
                >
                    <option value="all">Todos os Tipos</option>
                    <option value="CBHPM">CBHPM</option>
                    <option value="TUSS">TUSS</option>
                    <option value="AMB">AMB</option>
                    <option value="CUSTOM">Personalizada</option>
                </select>
                <select
                    value={statusFilter}
                    onChange={e => setStatusFilter(e.target.value)}
                    className="select select-bordered select-sm"
                >
                    <option value="all">Todos os Status</option>
                    <option value="ACTIVE">Ativa</option>
                    <option value="INACTIVE">Inativa</option>
                    <option value="DRAFT">Rascunho</option>
                </select>
            </div>

            {filteredTables.length === 0 ? (
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-12 text-center">
                    <MdTableChart className="text-6xl text-base-content/20 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-base-content mb-2">
                        Nenhuma tabela encontrada...
                    </h3>
                    <p className="text-base-content/60 mb-6">
                        Crie sua primeira tabela de preços
                    </p>
                    {canCreate && (
                        <button
                            onClick={() =>
                                navigate('/billing/price-tables/new')
                            }
                            className="btn btn-secondary gap-2"
                        >
                            <MdAdd className="text-lg" />
                            Nova Tabela
                        </button>
                    )}
                </div>
            ) : (
                <div className="overflow-x-auto bg-base-100 rounded-xl border border-base-300/50">
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Nome</th>
                                <th>Tipo</th>
                                <th>Convênio</th>
                                <th>Status</th>
                                <th>Itens</th>
                                <th>Validade</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredTables.map(table => (
                                <tr
                                    key={table.id}
                                    className="hover cursor-pointer"
                                    onClick={() =>
                                        navigate(
                                            `/billing/price-tables/${table.id}`
                                        )
                                    }
                                >
                                    <td>
                                        <div className="font-semibold">
                                            {table.name}
                                        </div>
                                        {table.version && (
                                            <div className="text-xs text-base-content/60">
                                                v{table.version}
                                            </div>
                                        )}
                                    </td>
                                    <td>
                                        <span className="badge badge-ghost">
                                            {PRICE_TABLE_TYPE_LABELS[table.type]}
                                        </span>
                                    </td>
                                    <td>{table.healthPlan?.name || '-'}</td>
                                    <td>
                                        <span
                                            className={`badge ${PRICE_TABLE_STATUS_COLORS[table.status]}`}
                                        >
                                            {PRICE_TABLE_STATUS_LABELS[table.status]}
                                        </span>
                                    </td>
                                    <td>{table._count?.items || 0}</td>
                                    <td>
                                        {table.validFrom && table.validUntil ? (
                                            <span className="text-sm">
                                                {format(
                                                    new Date(table.validFrom),
                                                    'dd/MM/yy',
                                                    { locale: ptBR }
                                                )}{' '}
                                                -{' '}
                                                {format(
                                                    new Date(table.validUntil),
                                                    'dd/MM/yy',
                                                    { locale: ptBR }
                                                )}
                                            </span>
                                        ) : (
                                            '-'
                                        )}
                                    </td>
                                    <td>
                                        <div
                                            className="flex items-center gap-1"
                                            onClick={e => e.stopPropagation()}
                                        >
                                            {canUpdate && (
                                                <button
                                                    onClick={() =>
                                                        navigate(
                                                            `/billing/price-tables/${table.id}/edit`
                                                        )
                                                    }
                                                    className="btn btn-ghost btn-xs"
                                                    title="Editar"
                                                >
                                                    <MdEdit className="text-lg" />
                                                </button>
                                            )}
                                            {canCreate && (
                                                <button
                                                    onClick={() =>
                                                        navigate(
                                                            `/billing/price-tables/${table.id}/duplicate`
                                                        )
                                                    }
                                                    className="btn btn-ghost btn-xs"
                                                    title="Duplicar"
                                                >
                                                    <MdContentCopy className="text-lg" />
                                                </button>
                                            )}
                                            {canDelete && (
                                                <button
                                                    onClick={() =>
                                                        setDeleteId(table.id)
                                                    }
                                                    className="btn btn-ghost btn-xs text-error"
                                                    title="Excluir"
                                                >
                                                    <MdDelete className="text-lg" />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {deleteId && (
                <div className="modal modal-open">
                    <div className="modal-box">
                        <h3 className="font-bold text-lg">Confirmar exclusão</h3>
                        <p className="py-4">
                            Tem certeza que deseja excluir esta tabela de preços?
                            Esta ação não pode ser desfeita.
                        </p>
                        <div className="modal-action">
                            <button
                                onClick={() => setDeleteId(null)}
                                className="btn btn-ghost"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={() => handleDelete(deleteId)}
                                className="btn btn-error"
                            >
                                Excluir
                            </button>
                        </div>
                    </div>
                    <div
                        className="modal-backdrop"
                        onClick={() => setDeleteId(null)}
                    ></div>
                </div>
            )}
        </div>
    )
}
